import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Roles that, even when principal_type === "employee", are allowed to
// access the admin sidebar (see lib/routes.js → canAccessAdmin).
const ADMIN_CAPABLE_PORTAL_ROLES = new Set(["senior_management", "hod"]);

/**
 * 1-click "Switch to My Portal" / "Switch to HR Console" button.
 *
 * Two modes:
 *
 *  1. **Dual-principal users** (e.g. super_admin + linked employee record):
 *     Calls POST /api/auth/switch-principal which re-issues auth cookies
 *     with the OTHER principal_type, then navigates.
 *
 *  2. **Single-principal admin-capable employees** (Senior Management, HOD —
 *     Phase 15.12 + 15.16): no cookie swap needed; they already have
 *     dual-access. Just toggle the route between `/` and `/my-portal/dashboard`.
 */
export default function PrincipalSwitcher({ tone = "dark" }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);

  const counterpartType = user?.counterpart_principal_type;
  // Single-principal SM/HOD users: synthesize a virtual switcher
  const isPortalAdminCapable =
    user?.principal_type === "employee" &&
    (ADMIN_CAPABLE_PORTAL_ROLES.has(user?.role) || user?.is_senior_management);

  if (!counterpartType && !isPortalAdminCapable) return null;

  // Decide what the toggle will do — route-only (SM/HOD) or principal-swap (dual)
  const onPortalUrl = location.pathname.startsWith("/my-portal")
                       || location.pathname.startsWith("/me");
  let label, subLabel, mode;
  if (counterpartType) {
    // Existing dual-principal behaviour
    const targetIsAdmin = counterpartType === "user";
    label = targetIsAdmin ? "Switch to HR Console" : "Switch to My Portal";
    subLabel = targetIsAdmin
      ? user?.counterpart_role?.replace(/_/g, " ")
      : `as ${user?.counterpart_role || "Employee"}`.replace(/_/g, " ");
    mode = "swap";
  } else {
    // SM / HOD — route-only toggle
    label = onPortalUrl ? "Switch to HR Console" : "Switch to My Portal";
    subLabel = `as ${(user?.role || "").replace(/_/g, " ")}`;
    mode = "route";
  }

  const onClick = async () => {
    setBusy(true);
    try {
      if (mode === "route") {
        // No cookie swap — both views legal for SM/HOD. Just navigate.
        navigate(onPortalUrl ? "/" : "/my-portal/dashboard", { replace: true });
        toast.success(`Switched to ${onPortalUrl ? "HR Console" : "My Portal"}`);
      } else {
        const { data } = await api.post("/auth/switch-principal");
        toast.success(`Switched to ${data.principal_type === "user" ? "HR Console" : "My Portal"}`);
        if (refresh) await refresh();
        navigate(data.principal_type === "employee" ? "/my-portal/dashboard" : "/", { replace: true });
        window.location.reload();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Switch failed");
    } finally {
      setBusy(false);
    }
  };

  const baseCls = tone === "dark"
    ? "border border-slate-700 hover:bg-slate-800 hover:text-white text-slate-300"
    : "border border-slate-300 hover:bg-slate-50 hover:text-slate-900 text-slate-600";

  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid="principal-switcher-btn"
      className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-md transition-colors ${baseCls} disabled:opacity-50`}
      title="Switch between HR Console and My Portal — same email, two views"
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
      ) : (
        <ArrowLeftRight className="h-3.5 w-3.5 flex-shrink-0" />
      )}
      <div className="flex flex-col items-start text-left flex-1 min-w-0">
        <span className="leading-tight">{label}</span>
        {subLabel && <span className="text-[9px] uppercase tracking-wider opacity-60 truncate">{subLabel}</span>}
      </div>
    </button>
  );
}
